import React from 'react'
import { withLayout } from '../../shared/Styles.js'
import Picture from '../../components/picture.js'
import { Heading } from '../../components/heading.js'
import { Text } from '../../components/text.js'
import Title from '../Title/index.js'
import type { Shape } from '../../types/props'
import { cn } from '../../helpers/utils.js'
import type { Media, Img } from '../../types/props'

export interface AttractionItem {
  icon?: Img
  title: string
  description: string
}

interface GraphicAttractionBlockProps extends React.HTMLAttributes<HTMLElement> {
  data: {
    shape?: Shape
    sectionTitle?: string
    imageUrlPc?: Media
    imageUrlMob?: Media
    imageTitle?: string
    imageDescription?: string
    attractionItems?: AttractionItem[]
  }
}

const GraphicAttractionBlock = React.forwardRef<HTMLElement, GraphicAttractionBlockProps>(
  ({ data, className, ...rest }, ref) => {
    const { shape, imageUrlPc, imageUrlMob, sectionTitle, imageTitle, imageDescription, attractionItems } = data

    return (
      <section
        ref={ref}
        data-ui-component-id="GraphicAttractionBlock"
        {...rest}
        className={cn('text-info-primary relative', className)}
      >
        {sectionTitle && <Title data={{ title: sectionTitle }} />}
        <div
          className={cn('tablet:aspect-[1664/560] relative aspect-[358/480] w-full overflow-hidden', {
            'rounded-box': shape === 'rounded',
          })}
        >
          <Picture
            source={`${imageUrlPc?.url}, ${imageUrlMob?.url} 767`}
            className="inset-0"
            imgClassName="h-full object-cover"
            alt={imageUrlPc?.alt || ''}
          />
          <div className="lg-desktop:p-8 absolute inset-0 z-[1] flex flex-col justify-end p-4">
            <Heading as="h3" className="text-white" size={3} html={imageTitle} />
            <Text as="p" className="text-white" size={2} html={imageDescription} />
          </div>
        </div>
        <div className="tablet:gap-4 tablet:grid-cols-4 tablet:mt-[10px] mt-4 grid grid-cols-2 gap-3">
          {attractionItems?.map((item, index) => (
            <div
              key={index}
              className={cn(
                'lg-desktop:p-8 desktop:p-6 desktop:gap-16 flex flex-col justify-between gap-12 bg-white p-4',
                { 'rounded-box': shape === 'rounded' }
              )}
            >
              <div className="lg-desktop:h-[36px] flex h-[30px] items-center justify-between gap-3">
                <Text as="p" className="lg-desktop:text-[18px] tracking text-[14px] font-semibold" html={item.title} />
                <Picture
                  className="lg-desktop:size-[36px] size-[30px] shrink-0 object-cover"
                  source={item.icon?.url}
                  alt={item.icon?.alt}
                  imgClassName="w-full h-full object-cover"
                />
              </div>
              <Text
                as="p"
                className="lg-desktop:text-[24px] tracking text-[20px] leading-tight"
                html={item.description}
              />
            </div>
          ))}
        </div>
      </section>
    )
  }
)

GraphicAttractionBlock.displayName = 'GraphicAttractionBlock'
export default withLayout(GraphicAttractionBlock)
export type { GraphicAttractionBlockProps }
